/**
 * Request Deduplication Utility
 * ─────────────────────────────
 * Two-layer deduplication for axios GET requests:
 *
 *   1) **In-flight dedup** (the important one) — if a GET to the same
 *      URL+params is already in flight, ANY subsequent caller within
 *      the same tick gets the SAME Promise.  Only one HTTP request
 *      hits the wire.  This is what kills the "useEffect-on-mount
 *      fires the same endpoint twice from two different components"
 *      pattern (Billing tab: notes-ready 1.67MB called 2× back-to-back,
 *      claims/queue called 2×, etc.).
 *
 *   2) **Short-window response cache** — keeps the last response
 *      around for 500 ms so an immediately-following caller doesn't
 *      have to refetch (handles cases where the first response
 *      resolved a hair before the duplicate caller arrived).
 *
 * Mutations (POST/PUT/PATCH/DELETE) are never deduped — they have
 * side effects.  Only GETs.
 *
 * To opt OUT for a specific call (e.g. a manual "refresh" button
 * that legitimately wants a fresh request even if one is in flight):
 *
 *   axios.get(url, { skipDeduplication: true })
 *
 * Implementation note: we monkey-patch `axios.get` directly rather
 * than using request interceptors, because axios's interceptor API
 * does not provide a clean way to "return a different Promise
 * instead of making the request".  Wrapping the method is the most
 * compatible pattern (it works for both `axios.get(url)` and
 * `axios.get(url, config)` callers across the entire codebase).
 */

// key → { promise, timestamp, responseSnapshot? }
const inflight = new Map();

const CONFIG = {
  // How long to keep a completed response around so a slightly-later
  // duplicate caller can reuse it (ms).
  responseCacheMs: 500,
  // Whether to log dedup hits for debugging.
  debug: false,
};

function makeKey(url, config) {
  const params = config?.params ? JSON.stringify(config.params) : '';
  // Include selected headers that affect response semantics
  // (Authorization is tied to the user so different users would key
  // separately — but per browser tab the user is constant so we can
  // skip it).
  return `GET:${url}:${params}`;
}

function snapshotResponse(response) {
  // Clone shallow — call sites usually read `.data` directly, but
  // some also read `.status` and `.headers`.  We do NOT deep-clone
  // `.data` because (a) it can be huge (notes-ready = 1.67 MB) and
  // (b) callers don't mutate the response payload — they store it
  // into React state.  Sharing the same object reference between
  // duplicate callers is actually a perf win.
  return {
    data: response.data,
    status: response.status,
    headers: response.headers,
    config: response.config,
    statusText: response.statusText,
    request: response.request,
  };
}

export function setupRequestDeduplication(axiosInstance) {
  const originalGet = axiosInstance.get.bind(axiosInstance);

  axiosInstance.get = function dedupedGet(url, config) {
    // Explicit opt-out (e.g. user-initiated refresh).
    if (config && config.skipDeduplication) {
      return originalGet(url, config);
    }

    const key = makeKey(url, config);
    const existing = inflight.get(key);

    if (existing) {
      // (a) Still in-flight → reuse the SAME Promise.
      if (existing.promise) {
        if (CONFIG.debug) {
          // eslint-disable-next-line no-console
          console.log(`[DEDUP] in-flight hit: ${key}`);
        }
        return existing.promise;
      }
      // (b) Recently completed (within responseCacheMs) → reuse
      //     the cached response without firing the request.
      if (existing.responseSnapshot
          && (Date.now() - existing.timestamp) < CONFIG.responseCacheMs) {
        if (CONFIG.debug) {
          // eslint-disable-next-line no-console
          console.log(`[DEDUP] cache hit (${Date.now() - existing.timestamp}ms): ${key}`);
        }
        return Promise.resolve(existing.responseSnapshot);
      }
    }

    // Fire the actual request.  Wrap so we can settle the inflight
    // tracker on both success and failure paths.
    const promise = originalGet(url, config).then(
      (response) => {
        // Replace the inflight Promise entry with a short-lived
        // response-cache entry.
        inflight.set(key, {
          promise: null,
          responseSnapshot: snapshotResponse(response),
          timestamp: Date.now(),
        });
        // Schedule cache eviction.
        setTimeout(() => {
          const e = inflight.get(key);
          // Only delete if this is still the same entry (not a newer
          // request that has started since).
          if (e && e.promise === null && e.timestamp <= Date.now()) {
            inflight.delete(key);
          }
        }, CONFIG.responseCacheMs + 10);
        return response;
      },
      (error) => {
        // On error, drop the inflight entry immediately so retries
        // hit the network.
        inflight.delete(key);
        throw error;
      },
    );

    inflight.set(key, {
      promise,
      responseSnapshot: null,
      timestamp: Date.now(),
    });
    return promise;
  };

  if (CONFIG.debug) {
    // eslint-disable-next-line no-console
    console.log('[DEDUP] axios.get wrapped with in-flight + response-cache dedup');
  }
}

/**
 * Clear all in-flight/cached entries.  Call on logout so a different
 * user's responses are not handed to the next session.
 */
export function clearPendingRequests() {
  inflight.clear();
}

/**
 * Get stats about pending requests (for debugging).
 */
export function getPendingRequestStats() {
  return {
    count: inflight.size,
    keys: Array.from(inflight.keys()),
  };
}

export function setDebugMode(enabled) {
  CONFIG.debug = enabled;
}

export default {
  setupRequestDeduplication,
  clearPendingRequests,
  getPendingRequestStats,
  setDebugMode,
};
